/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Breadcrumb,
  BreadcrumbItem,
  Skeleton,
  Tab,
  TabTitleText,
} from '@patternfly/react-core';
import { useTranslation } from 'react-i18next';
import { observer, useLocalStore } from 'mobx-react';
import { AlertGroupProvider } from '@ole-ui/ole-ui-components';
import { Helmet } from 'react-helmet';
import { Link } from 'react-mobx-router5';
import { CourseSubformat } from 'types';

import useMobxStores from 'hooks/useMobxStores';
import { KALTURA_CONFIGS } from 'config/constants';
import ModalVideoPlayer from 'components/ModalVideoPlayer';
import AvailableLanguages from 'components/Course/OverviewTab/AvailableLanguages';
import Objectives from 'components/Course/OverviewTab/Objectives';
import Prerequisites from 'components/Course/OverviewTab/Prerequisites';
import SidebarDetails from 'components/Course/OverviewTab/SidebarDetails';
import Description from 'components/Course/OverviewTab/Description';
import ExploreSubscriptions from 'components/ExploreSubscriptions';
import PremiumSessionsList from 'components/PremiumSessions';
import { extractFromSlug } from 'helpers/utils';
import LiveClassTab from 'components/Course/LiveClassTab';
import { LandingTab } from 'components/Landing/types';

import usePremiumSessionsHandlers from 'components/Landing/hooks/usePremiumSessionsHandlers';
import { LandingLayout } from 'components/Landing/LandingLayout';
import LandingPageSkeleton from 'components/Landing/LandingPageSkeleton';
import ErrorState from 'components/Error/ErrorState';
import SidebarButtons from './SidebarButtons';
import { CourseLandingTab } from './types';
import useCourseLandingContent from './hooks/useCourseLandingContent';
import useCourseLandingRedirectAndData from './hooks/useCourseLandingRedirectAndData';
import useCourseLandingTabs from './hooks/useCourseLandingTabs';
import useLiveClassEnrollmentAlert from './hooks/useLiveClassEnrollmentAlert';

import CourseLandingStore from './store';

const WhatsNextOfferings = React.lazy(
  () => import('components/Course/WhatsNextOfferings'),
);

const CourseLanding = () => {
  const { t } = useTranslation();
  const { addAlert } = AlertGroupProvider.useAlertGroupContext();

  const {
    routerStore,
    uiStore,
    userStore,
    catalogStore,
    progressStore,
    vocabularyStore,
    featuresStore,
    eventStore,
    liveClassStore,
  } = useMobxStores();

  const { route } = routerStore;
  const { course: courseSlug, isPremiumSessionCourseWithoutEntry } = (
    route as any
  ).params;

  // Redirect legacy premium session URLs to the new premium:sessions:overview route
  if (courseSlug && isPremiumSessionCourseWithoutEntry) {
    routerStore.navigate('premium:sessions:overview', {
      session: courseSlug,
      tab: LandingTab.PremiumSessions,
    });
    return null;
  }

  const {
    audienceLabels,
    courseInfo,
    courseMetadata,
    coursePremiumSessionCollateral,
    courseProgress,
    currentVersion,
    externalLoadingError,
    fetchOverviewData,
    isDataLoaded,
    isError,
    isInaccessible,
    isLoading,
    language,
    labGradeResults,
    productLabels,
    title,
    topicLabels,
    translations,
    versions,
  } = useLocalStore(
    () =>
      new CourseLandingStore(
        uiStore,
        catalogStore,
        vocabularyStore,
        progressStore,
        userStore,
        featuresStore,
        false,
      ),
  );

  const { code: slugCode, version: slugVersion } = extractFromSlug(
    courseSlug || '',
  );

  // Resolve on every render (do not memoize on catalogStore ref): MobX updates the
  // catalog after load, and useMemo([catalogStore]) would stay stale on direct URL hits.
  const bundleCatalogEntry =
    courseSlug && catalogStore.hasCatalogLoaded
      ? catalogStore.getCatalogEntryForCourse(
          slugCode.toLowerCase(),
          (slugVersion ?? '').toLowerCase(),
        )
      : undefined;

  const isBundleCourse = Boolean(
    (bundleCatalogEntry &&
      String(bundleCatalogEntry.format).toLowerCase() === 'bundle') ||
      (isDataLoaded &&
        courseMetadata &&
        String(courseMetadata.format).toLowerCase() === 'bundle'),
  );

  const publishedBundleLanguages = (() => {
    if (!isBundleCourse || !courseSlug) return [];
    const { code } = extractFromSlug(courseSlug);
    const byVersion =
      catalogStore.groupedCatalogEntries[code.toLowerCase()] || {};
    return [...new Set(Object.values(byVersion).map((e) => e.language))];
  })();

  const { selectedVersion, setSelectedVersion, redirectToPremiumLanding } =
    useCourseLandingRedirectAndData({
      courseSlug,
      catalogStore,
      uiStore,
      routerStore,
      liveClassStore,
      fetchOverviewData,
      externalLoadingError,
    });

  const {
    handleBulkScheduleButton,
    handleCancelBulkScheduling,
    handleConfirmEnrollment,
    handleSelect,
    hasPremiumSessions,
    hasPremiumSessionsInAnyVersion,
    isEnrolling,
    isModalOpen,
    liveSessionListRef,
    premiumSessionAggregate,
    selectedPremiumSessions,
    selectionMode,
    sessionCount,
    setIsModalOpen,
    slugsWithPremiumSessions,
  } = usePremiumSessionsHandlers(courseSlug);

  const {
    activeTabKey,
    handleTabClick,
    showLiveSessionsTab,
    showLiveClassTab,
    showWhatsNextTab,
  } = useCourseLandingTabs({
    courseSlug,
    route: route as { params?: Record<string, string> },
    routerStore,
    isInaccessible,
    hasPremiumSessionsInAnyVersion,
    isBundleCourse,
  });

  const {
    firstVideoEntry,
    contentForLang,
    totalVideos,
    progress,
    formattedProgress,
    nothingToShow,
  } = useCourseLandingContent({
    courseSlug,
    courseInfo,
    courseMetadata,
    language,
    translations,
    progressStore,
    isBundleCourse,
  });

  const availableLanguagesList = useMemo(() => {
    const fromMetadata = courseMetadata?.translations;
    if (fromMetadata?.length) return fromMetadata;
    if (translations?.length) return translations;
    return publishedBundleLanguages;
  }, [courseMetadata?.translations, translations, publishedBundleLanguages]);

  const onBundleLaunchComplete = useCallback(async () => {
    if (!courseSlug) return;
    try {
      await progressStore.setCourseProgress(courseSlug);
    } catch (error) {
      console.error('Cannot complete course:', error);
    }
  }, [courseSlug, progressStore]);

  useLiveClassEnrollmentAlert({ liveClassStore, addAlert });

  useEffect(() => {
    if (!courseSlug || !catalogStore.hasCatalogLoaded || isBundleCourse) {
      return;
    }
    liveClassStore.getOfferingEnrolledLiveClass(courseSlug);
  }, [
    courseSlug,
    catalogStore.hasCatalogLoaded,
    isBundleCourse,
    liveClassStore,
  ]);

  if (redirectToPremiumLanding) {
    return null;
  }

  if (isError) {
    return (
      <ErrorState
        showExploreSubscriptions={
          isInaccessible &&
          userStore.isRHLSTrialSubscriber &&
          userStore.isRedHatLead
        }
        onRetry={() => {
          if (courseSlug && selectedVersion) {
            const { code } = extractFromSlug(courseSlug);
            fetchOverviewData(code, selectedVersion);
          }
        }}
        onSupportClick={() => {
          eventStore.toggleDrawer('support_form');
        }}
      />
    );
  }

  if (isDataLoaded && nothingToShow) {
    routerStore.navigate(
      'courses:view',
      { course: courseSlug },
      // @ts-ignore
      { replace: true },
    );
    return null;
  }

  const liveClassTabTitle =
    liveClassStore.completedPerSkuQuotaAllowed > 0
      ? `${t('Live class')} (${liveClassStore.completedPerSkuQuotaUsed}/${
          liveClassStore.completedPerSkuQuotaAllowed
        })`
      : t('Live class');

  return (
    <LandingLayout>
      {isLoading ? (
        <LandingPageSkeleton
          breadcrumbParentLink={
            <Link
              href
              routeName="catalog"
              routeParams={catalogStore.lastCatalogParams}
            >
              {t('Catalog')}
            </Link>
          }
        />
      ) : (
        isDataLoaded && (
          <>
            <Helmet title={`${title} | Overview - Red Hat Learning`} />
            <LandingLayout.Breadcrumb>
              <Breadcrumb>
                <BreadcrumbItem>
                  <Link
                    href
                    routeName="catalog"
                    routeParams={catalogStore.lastCatalogParams}
                  >
                    {t('Catalog')}
                  </Link>
                </BreadcrumbItem>
                <BreadcrumbItem isActive>{title}</BreadcrumbItem>
              </Breadcrumb>
            </LandingLayout.Breadcrumb>
            <LandingLayout.Header
              title={title}
              subtitle={
                courseMetadata?.subformat === CourseSubformat.CompReview ? (
                  <h4>{t('Comprehensive review')}</h4>
                ) : null
              }
            />
            <LandingLayout.Content>
              <LandingLayout.Main>
                <LandingLayout.Tabs
                  mountOnEnter
                  isBox
                  activeKey={activeTabKey}
                  onSelect={handleTabClick}
                >
                  <Tab
                    eventKey={CourseLandingTab.Overview}
                    title={<TabTitleText>{t('Overview')}</TabTitleText>}
                    aria-label={t(
                      'Course overview containing detailed information',
                    )}
                    data-analytics-id="overview-tab-course-overview-view-lp"
                  >
                    {progress > 0 && (
                      <LandingLayout.Progress>
                        {t('Progress: {{formattedProgress}}%', {
                          formattedProgress,
                        })}
                      </LandingLayout.Progress>
                    )}
                    {isBundleCourse && (
                      <>
                        <LandingLayout.ContentHeading>
                          {t('Important course launching information')}
                        </LandingLayout.ContentHeading>
                        <LandingLayout.ContentText>
                          {t(
                            'Click Launch to begin your course. Please note that this course will open in a separate tab.',
                          )}
                        </LandingLayout.ContentText>
                      </>
                    )}
                    {contentForLang && (
                      <>
                        <Description description={contentForLang.description} />
                        <Prerequisites
                          prerequisites={contentForLang.prerequisites}
                        />
                        <Objectives objectives={contentForLang.objectives} />
                      </>
                    )}
                    <AvailableLanguages translations={availableLanguagesList} />
                  </Tab>

                  <Tab
                    isHidden={!showLiveSessionsTab}
                    eventKey={CourseLandingTab.PremiumSessions}
                    title={
                      <TabTitleText>
                        {t('Live sessions ({{sessionCount}})', {
                          sessionCount,
                        })}
                      </TabTitleText>
                    }
                    aria-label={t('View the live sessions for this course')}
                    data-analytics-id="livesessions-tab-course-overview-view-lp"
                  >
                    <div ref={liveSessionListRef} />
                    <PremiumSessionsList
                      coursePremiumSessionCollateral={
                        coursePremiumSessionCollateral
                      }
                      courseTitle={title}
                      showObjectivesFromCollateral={false}
                      isModalOpen={isModalOpen}
                      onCardSelect={handleSelect}
                      onConfirmEnrollment={handleConfirmEnrollment}
                      premiumSessionAggregate={premiumSessionAggregate}
                      selectedPremiumSessions={selectedPremiumSessions}
                      selectionMode={selectionMode}
                      setIsModalOpen={setIsModalOpen}
                      slugsWithPremiumSessions={slugsWithPremiumSessions}
                    />
                  </Tab>

                  <Tab
                    isHidden={!showLiveClassTab}
                    eventKey={CourseLandingTab.LiveClass}
                    title={<TabTitleText>{liveClassTabTitle}</TabTitleText>}
                    data-analytics-id="live-class-tab-course-overview-view-lp"
                  >
                    <LiveClassTab
                      description={contentForLang?.description || ''}
                      isInaccessible={isInaccessible}
                    />
                  </Tab>

                  <Tab
                    isHidden={!showWhatsNextTab}
                    eventKey={CourseLandingTab.WhatsNext}
                    title={<TabTitleText>{t("What's next")}</TabTitleText>}
                    aria-label={t('Details on what to do after this course')}
                    data-analytics-id="whatsnext-tab-course-overview-view-lp"
                  >
                    <React.Suspense
                      fallback={
                        <div className="whatsnext__loader">
                          <Skeleton width="50%" />
                          <Skeleton height="112px" />
                          <Skeleton height="112px" />
                          <Skeleton width="50%" />
                          <Skeleton height="112px" />
                        </div>
                      }
                    >
                      <WhatsNextOfferings
                        className="whatsnext__offerings"
                        relatedContents={courseMetadata?.related_contents}
                      />
                    </React.Suspense>
                  </Tab>
                </LandingLayout.Tabs>
              </LandingLayout.Main>

              <LandingLayout.Sidebar>
                <SidebarButtons
                  activeTab={activeTabKey}
                  courseSlug={courseSlug}
                  courseProgress={courseProgress}
                  currentVersion={currentVersion}
                  fetchOverviewData={fetchOverviewData}
                  hasPremiumSessions={hasPremiumSessions}
                  isEnrolling={isEnrolling}
                  isInaccessible={isInaccessible}
                  onCancelBulkScheduling={handleCancelBulkScheduling}
                  onBulkScheduleButton={handleBulkScheduleButton}
                  selectedPremiumSessions={selectedPremiumSessions}
                  selectionMode={selectionMode}
                  selectedVersion={selectedVersion}
                  setSelectedVersion={setSelectedVersion}
                  versions={versions}
                  setIsModalOpen={setIsModalOpen}
                  totalSessions={sessionCount}
                  isBundleCourse={isBundleCourse}
                  bundleReference={bundleCatalogEntry?.bundle_reference}
                  bundleSource={bundleCatalogEntry?.source}
                  onBundleLaunchComplete={onBundleLaunchComplete}
                />

                <ExploreSubscriptions />

                {firstVideoEntry && (
                  <>
                    <p>
                      {t('Watch the {{courseType}} overview video:', {
                        courseType:
                          courseMetadata?.subformat ===
                          CourseSubformat.CompReview
                            ? t('Comprehensive review').toLowerCase()
                            : '',
                      })}
                    </p>
                    <ModalVideoPlayer
                      entryId={firstVideoEntry.entry_id}
                      playerId={KALTURA_CONFIGS.player.uiConfId}
                      thumbnailImageProps={{
                        width: '100%',
                        alt: t('Course introduction for {{title}}', {
                          title,
                        }),
                      }}
                    />
                  </>
                )}

                <SidebarDetails
                  audienceLabels={audienceLabels}
                  certifications={courseMetadata?.certifications}
                  labGradeResults={labGradeResults}
                  language={language}
                  productLabels={productLabels}
                  totalGuidedExercises={courseMetadata?.total_ge}
                  topicLabels={topicLabels}
                  totalLabs={courseMetadata?.total_labs}
                  totalQuiz={courseMetadata?.total_quiz}
                  totalVideos={totalVideos}
                  totalDuration={courseMetadata?.total_duration}
                />
              </LandingLayout.Sidebar>
            </LandingLayout.Content>
          </>
        )
      )}
    </LandingLayout>
  );
};

export default observer(CourseLanding);
